import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@root/src/environments/environment';
import { AuthService } from '@app/services/auth.service';

export interface SatelliteTaskStatus {
  id: string;
  productId: string;
  productName?: string;
  satelliteType: number;
  status: number;
  statusName: string;
  createAt: string;
  originDate?: string;
  progressPercent: number;
  regionName?: string;
  indexStatus?: {
    isTiffComplete: boolean;
    isDbComplete: boolean;
    indexDetails: Array<{
      indexName: string;
      tiffGenerated: boolean;
      dbLoaded: boolean;
    }>;
  };
  lastUpdated?: string;
  errorMessage?: string;
}

export interface SatelliteTaskStatusResponse {
  data: SatelliteTaskStatus[];
  total: number;
}

export interface GetSatelliteTaskStatusRequest {
  take?: number;
  offset?: number;
  sortBy?: string;
  sort?: number;
  status?: number;
  satelliteType?: number;
  startDate?: string;
  endDate?: string;
  productId?: string;
}

export interface SimpleStatusParams {
  take?: number;
  offset?: number;
  status?: number;
  satelliteType?: number;
  startDate?: string;
  endDate?: string;
  productId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SatelliteTaskStatusService {
  private readonly baseUrl = `${environment.api}/satellite/SatelliteTaskStatus`;
  
  // Дефолтный ответ для случая ошибки
  private readonly defaultErrorResponse: SatelliteTaskStatusResponse = {
    data: [],
    total: 0
  };

  constructor(
    private http: HttpClient,
    private authService: AuthService
  ) {}

  private getAuthHeaders(): HttpHeaders | null {
    const token = this.authService.getToken();
    if (!token) {
      return null;
    }
    
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  private handleError<T>(defaultValue: T): (error: any) => Observable<T> {
    return () => of(defaultValue);
  }

  getTaskStatus(request: GetSatelliteTaskStatusRequest): Observable<SatelliteTaskStatusResponse> {
    const headers = this.getAuthHeaders();
    
    if (!headers) {
      return of(this.defaultErrorResponse);
    }
    
    return this.http.post<SatelliteTaskStatusResponse>(
      `${this.baseUrl}/GetTaskStatus`,
      request,
      { headers }
    ).pipe(
      catchError(this.handleError(this.defaultErrorResponse))
    );
  }

  getTaskStatusSimple(params: SimpleStatusParams): Observable<SatelliteTaskStatusResponse> {
    const headers = this.getAuthHeaders();
    
    if (!headers) {
      return of(this.defaultErrorResponse);
    }

    let httpParams = new HttpParams();
    
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined && value !== null) {
        httpParams = httpParams.set(key, String(value));
      }
    }
    
    return this.http.get<SatelliteTaskStatusResponse>(
      `${this.baseUrl}/GetTaskStatusSimple`,
      { headers, params: httpParams }
    ).pipe(
      catchError(this.handleError(this.defaultErrorResponse))
    );
  }
}